import { UUID } from '@indigohive/cogfy-types'
import { GetChatMessagesPageResultData } from '@indigohive/cogfy-types/endpoints/getChatMessagesPage'
import { GetCollectionByIdResult } from '@indigohive/cogfy-types/endpoints/getCollectionById'
import { GetAuthenticatedUserResult } from '@indigohive/cogfy-types/endpoints/getAuthenticatedUser'
import { GetChatByIdResult } from '@indigohive/cogfy-types/endpoints/getChatById'
import { isSameDay, parseISO } from 'date-fns'
import { ChatBubble, ChatDate, ChatMessageReaction, ChatMessageStatus } from '.'
import clsx from 'clsx'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

export type ChatPageMessagesContainerProps = {
  messages: GetChatMessagesPageResultData[]
  chat?: GetChatByIdResult
  collection?: GetCollectionByIdResult
  authenticatedUser?: GetAuthenticatedUserResult | null
  onMessageReaction?: () => void
  onUpdateMessageStatus?: (messageId: UUID) => void
  onMessageContextMenu?: (event: React.MouseEvent, messageId: UUID) => void
}

export function ChatPageMessagesContainer (props: ChatPageMessagesContainerProps) {
  const { messages, chat, collection, authenticatedUser } = props
  const { t } = useTranslation()

  if (!chat || !collection) return <></>

  return (
    <>
      {messages.map((message, index) => {
        const prevMessage = messages[index - 1]
        const nextMessage = messages[index + 1]
        const showDate = !nextMessage || !isSameDay(parseISO(nextMessage.sendDate), parseISO(message.sendDate))
        const senderName = getSenderName(t, message)
        const hideAvatar =
          prevMessage &&
          senderName === getSenderName(t, prevMessage) &&
          isSameDay(parseISO(prevMessage.sendDate), parseISO(message.sendDate))
        const fromAuthenticatedUser = message.user?.id === authenticatedUser?.id

        return (
          <div
            key={message.id}
            className="group"
          >
            {showDate && <ChatDate sendDate={message.sendDate} />}

            {message.role !== 'tool' && (
              <ChatBubble
                message={message}
                senderName={senderName ?? ''}
                fromAuthenticatedUser={fromAuthenticatedUser}
                onContextMenu={event => props.onMessageContextMenu?.(event, message.id)}
                className={clsx(messages?.[index - 1]?.role !== message.role && 'mt-4 leading-8')}
                color={message.user?.id === authenticatedUser?.id ? 'primary' : 'neutral'}
                avatarImageSrc={message.role === 'assistant' ? '/cogfy-avatar.png' : undefined}
                user={message.user?.id === authenticatedUser?.id ? undefined : message.user}
                sendDate={message.sendDate}
                hideAvatar={hideAvatar}
              />
            )}

            <ChatMessageStatus
              message={message}
              position={message.user?.id === authenticatedUser?.id ? 'end' : 'start'}
            />

            {message.role === 'assistant' && (
              <ChatMessageReaction
                chatId={chat.id}
                messageId={message.id}
                reaction={message.reaction?.type}
                onMessageReaction={props.onMessageReaction}
              />
            )}
          </div>
        )
      })}
    </>
  )
}

function getSenderName (
  translation: TFunction<'translation', undefined>,
  message: GetChatMessagesPageResultData
) {
  if (message.role === 'assistant') {
    return translation('Assistant')
  }

  if (message.role === 'user') {
    return message.user?.name
  }

  return message.user?.name
}
